import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  FiTool,
  FiHeart,
  FiMapPin,
  FiArrowRight,
  FiImage,
  FiTag,
  FiDroplet,
  FiZap,
  FiWind,
} from "react-icons/fi";
import OverviewSection, { HorizontalRow } from "./OverviewSection";
import useAsyncData from "./useAsyncData";
import {
  getNearbyProperties,
  getNearbyPosts,
  getOverviewPromotions,
  getInteriorCatalogs,
  getHomeServiceCategories,
  followProperty,
  unfollowProperty,
} from "../../../../api/customer/overviewApis";
import { showToast } from "../../../common/notification/Notification";

const assetUrl = process.env.REACT_APP_ASSET_URL || "";

export const resolveImage = (path, base = assetUrl) => {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : `${base}${path}`;
};

export const formatPrice = (value) => {
  const n = typeof value === "number" ? value : parseFloat(value);
  return n ? `₹${n.toLocaleString("en-IN")}` : "Price on request";
};

export const stripHtml = (html) => String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

/** Image with a fixed ratio box and a placeholder when the file is missing or fails to load. */
export const SafeImage = ({ src, alt, ratio = "16 / 9" }) => {
  const [failed, setFailed] = useState(false);
  return (
    <div className="ov-media" style={{ aspectRatio: ratio }}>
      {src && !failed ? (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <div className="ov-media__placeholder" role="img" aria-label={`${alt} (no image available)`}>
          <FiImage size={28} aria-hidden="true" />
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* 1. Properties Nearby                                                */
/* ------------------------------------------------------------------ */
export const PropertiesNearbySection = ({ token, location, locationReady, basePath = "/customer" }) => {
  const { data, loading, error, retry } = useAsyncData(
    () => getNearbyProperties(token, location),
    [token, location.city, location.state, location.areas.join(",")],
    Boolean(token) && locationReady
  );
  const [busyId, setBusyId] = useState(null);

  const toggleSave = async (event, property) => {
    event.preventDefault();
    event.stopPropagation();
    if (busyId) return;
    setBusyId(property.id);
    try {
      if (property.is_favorite && property.favorite_id) {
        await unfollowProperty(token, property.favorite_id);
        showToast("Property removed from your followed list", { type: "success" });
      } else {
        await followProperty(token, property.id);
        showToast("Property added to your followed list", { type: "success" });
      }
      retry(); // pick up the new favourite id / state from the server
    } catch (err) {
      showToast(err?.response?.data?.text || err.message || "Unable to update followed list", { type: "error" });
    } finally {
      setBusyId(null);
    }
  };

  const properties = data?.properties || [];
  return (
    <OverviewSection
      id="properties-nearby"
      title="Properties Nearby"
      description="Listings in your area, with the latest first."
      viewAllTo={`${basePath}/properties`}
      loading={loading || !locationReady}
      error={error}
      onRetry={retry}
      isEmpty={properties.length === 0}
    >
      <HorizontalRow label="Properties nearby">
        {properties.map((p) => {
          const image = resolveImage(p.images?.[0]?.image_url, data.assetUrl || assetUrl);
          const location_ = [p.locality, p.city?.name, p.state?.name].filter(Boolean).join(", ");
          const saved = Boolean(p.is_favorite);
          return (
            <article key={p.id} className="ov-card" role="listitem">
              <Link to={`${basePath}/properties/${p.id}`} className="ov-card__link" aria-label={`View ${p.title || "property"}`}>
                <SafeImage src={image} alt={p.title || "Property"} />
                <div className="ov-card__body">
                  <div className="ov-card__price">{formatPrice(p.budget ?? p.price)}</div>
                  <h3 className="ov-card__title" title={p.title}>
                    {p.title || "Property"}
                  </h3>
                  <p className="ov-card__meta">
                    <FiMapPin size={13} aria-hidden="true" /> <span>{location_ || "Location not specified"}</span>
                  </p>
                  <div className="ov-card__chips">
                    {p.type?.name && <span className="ov-chip">{p.type.name}</span>}
                    {p.purpose?.name && <span className="ov-chip ov-chip--soft">{p.purpose.name}</span>}
                  </div>
                </div>
              </Link>
              <button
                type="button"
                className={`ov-card__save ${saved ? "ov-card__save--on" : ""}`}
                aria-label={saved ? `Remove ${p.title || "property"} from followed` : `Save ${p.title || "property"}`}
                aria-pressed={saved}
                disabled={busyId === p.id}
                onClick={(e) => toggleSave(e, p)}
              >
                <FiHeart size={16} fill={saved ? "currentColor" : "none"} aria-hidden="true" />
              </button>
            </article>
          );
        })}
      </HorizontalRow>
    </OverviewSection>
  );
};

/* ------------------------------------------------------------------ */
/* 2. Posts Nearby                                                     */
/* ------------------------------------------------------------------ */
export const PostsNearbySection = ({ token, location, locationReady, basePath = "/customer" }) => {
  const { data, loading, error, retry } = useAsyncData(
    () => getNearbyPosts(token, location),
    [token, location.city, location.state],
    Boolean(token) && locationReady
  );
  const posts = data || [];

  return (
    <OverviewSection
      id="posts-nearby"
      title="Posts Nearby"
      description="Requirements and inquiries posted around you."
      viewAllTo={`${basePath}/posts`}
      loading={loading || !locationReady}
      error={error}
      onRetry={retry}
      isEmpty={posts.length === 0}
    >
      <HorizontalRow label="Posts nearby">
        {posts.map((post) => {
          const author = post.partner || post.customer;
          const name = [author?.first_name, author?.last_name].filter(Boolean).join(" ") || "Community member";
          const location_ = [post.city_name, post.state_name].filter(Boolean).join(", ");
          return (
            <article key={post.id} className="ov-card ov-card--post" role="listitem">
              <Link to={`${basePath}/posts/${post.id}`} className="ov-card__link" aria-label={`View post by ${name}`}>
                <div className="ov-card__body ov-card__body--post">
                  <div className="ov-author">
                    <span className="ov-author__avatar" aria-hidden="true">
                      {name.charAt(0).toUpperCase()}
                    </span>
                    <span className="ov-author__name">{name}</span>
                  </div>
                  {post.service && <span className="ov-chip align-self-start">{post.service}</span>}
                  <p className="ov-card__clamp">{stripHtml(post.description) || "No description available"}</p>
                  {location_ && (
                    <p className="ov-card__meta mt-auto">
                      <FiMapPin size={13} aria-hidden="true" /> <span>{location_}</span>
                    </p>
                  )}
                  <span className="ov-card__cta">
                    View <FiArrowRight size={13} aria-hidden="true" />
                  </span>
                </div>
              </Link>
            </article>
          );
        })}
      </HorizontalRow>
    </OverviewSection>
  );
};

/* ------------------------------------------------------------------ */
/* 3. Promotions                                                       */
/* ------------------------------------------------------------------ */
export const PromotionsSection = ({ token, basePath = "/customer", title = "Promotions", description = "Featured projects and member offers from our partners." }) => {
  const { data, loading, error, retry } = useAsyncData(() => getOverviewPromotions(token), [token], Boolean(token));
  const promotions = (data || []).slice(0, 8);

  return (
    <OverviewSection
      id="promotions"
      title={title}
      description={description}
      viewAllTo={`${basePath}/promotions`}
      loading={loading}
      error={error}
      onRetry={retry}
      isEmpty={promotions.length === 0}
    >
      <HorizontalRow label="Promotions">
        {promotions.map((promo) => {
          const image = resolveImage(promo.propertyImages?.[0]?.image_url);
          const place = [promo.city_name, promo.state_name].filter(Boolean).join(", ");
          return (
            <article key={promo.id} className="ov-card" role="listitem">
              <Link to={`${basePath}/promotions/${promo.id}`} className="ov-card__link" aria-label={`View ${promo.project_name || "offer"}`}>
                <div className="ov-media-wrap">
                  <SafeImage src={image} alt={promo.project_name || "Offer"} />
                  <span className="ov-badge">
                    <FiTag size={12} aria-hidden="true" /> Special Offer
                  </span>
                </div>
                <div className="ov-card__body">
                  <h3 className="ov-card__title" title={promo.project_name}>
                    {promo.project_name || "Offer"}
                  </h3>
                  {place && (
                    <p className="ov-card__meta">
                      <FiMapPin size={13} aria-hidden="true" /> <span>{place}</span>
                    </p>
                  )}
                  {promo.starting_cost ? <p className="ov-card__price">From {formatPrice(promo.starting_cost)}</p> : null}
                  <div className="ov-card__foot">
                    <span className="ov-card__limited">Limited Time</span>
                    <span className="ov-card__cta">
                      Explore <FiArrowRight size={13} aria-hidden="true" />
                    </span>
                  </div>
                </div>
              </Link>
            </article>
          );
        })}
      </HorizontalRow>
    </OverviewSection>
  );
};

/* ------------------------------------------------------------------ */
/* 4. Interior Design                                                  */
/* ------------------------------------------------------------------ */
export const InteriorDesignSection = ({ token }) => {
  const { data, loading, error, retry } = useAsyncData(() => getInteriorCatalogs(token, { page_size: 6 }), [token], Boolean(token));
  const catalogs = data || [];

  return (
    <OverviewSection
      id="interior-design"
      title="Interior Design"
      description="Design catalogs from interior partners."
      viewAllTo="/customer/interior-design"
      loading={loading}
      error={error}
      onRetry={retry}
      isEmpty={catalogs.length === 0}
    >
      <HorizontalRow label="Interior design">
        {catalogs.map((c) => {
          const image = resolveImage(c.catalog_images?.[0]?.image_url);
          return (
            <article key={c.id} className="ov-card" role="listitem">
              <Link to={`/customer/interior-design/${c.id}`} className="ov-card__link" aria-label={`View ${c.type?.name || "interior design"}`}>
                <SafeImage src={image} alt={c.type?.name || "Interior design"} />
                <div className="ov-card__body">
                  <h3 className="ov-card__title">{c.type?.name || "Interior"}</h3>
                  <p className="ov-card__clamp ov-card__clamp--2">{stripHtml(c.description)}</p>
                  {c.price ? <p className="ov-card__price">{formatPrice(c.price)}</p> : null}
                </div>
              </Link>
            </article>
          );
        })}
      </HorizontalRow>
    </OverviewSection>
  );
};

/* ------------------------------------------------------------------ */
/* 5. Home Services (service categories, same API as mobile)          */
/* ------------------------------------------------------------------ */
const categoryIcon = (name) => {
  const n = String(name || "").toLowerCase();
  if (n.includes("plumb")) return FiDroplet;
  if (n.includes("electric")) return FiZap;
  if (n.includes("clean")) return FiWind;
  return FiTool;
};

const TINTS = ["blue", "green", "amber", "purple", "red"];

export const HomeServicesSection = ({ token }) => {
  const { data, loading, error, retry } = useAsyncData(() => getHomeServiceCategories(token), [token], Boolean(token));
  const categories = (data || []).filter((c) => c.active !== false && c.deleted !== true);

  return (
    <OverviewSection
      id="home-services"
      title="Home Services"
      description="Trusted providers for your home, by category."
      viewAllTo="/customer/services"
      loading={loading}
      error={error}
      onRetry={retry}
      isEmpty={categories.length === 0}
    >
      <div className="ov-grid">
        {categories.slice(0, 12).map((c, i) => {
          const Icon = categoryIcon(c.name);
          return (
            <Link key={c.id} to={`/customer/services?category=${c.id}`} className={`ov-service ov-tint--${TINTS[i % TINTS.length]}`}>
              <span className="ov-service__icon" aria-hidden="true">
                <Icon size={22} />
              </span>
              <span className="ov-service__name">{c.name}</span>
            </Link>
          );
        })}
      </div>
    </OverviewSection>
  );
};
